import { FieldError, UseFormReturn } from 'react-hook-form'
import { CheckoutForm, FormFields } from 'shop/components/Checkout/types'
import { inputCharactersLimitValidator } from './utils'
import FormInput from '../Inputs/FormInput'
import styled, { CSSObject } from '@emotion/styled'
import { useRef } from 'react'
import { handleFormFieldCartUpdate } from './formCartUpdates'
import { useConsumerCart } from 'shop/hooks'
import { useFocusOutListener } from 'shop/hooks/useFocusOutListener'

type CustomFieldProps = {
  fieldName: string
  required?: boolean
  placeholder?: string
  formHandle: UseFormReturn<FormFields>
  error?: FieldError
  prevCustomFieldRef: React.MutableRefObject<
    CheckoutForm['customFieldValue'] | undefined
  >
}

const CustomField = ({
  fieldName,
  required = false,
  placeholder = '',
  formHandle,
  error,
  prevCustomFieldRef
}: CustomFieldProps) => {
  const { watch, register } = formHandle
  const customFieldValue = watch('customFieldValue')
  const sectionRef = useRef<HTMLDivElement | null>(null)

  const { updateConsumerCart } = useConsumerCart()

  // Set listener for when the form section loses focus to trigger update cart and collapse.
  const handleFocusOut = async (e: FocusEvent) => {
    handleFormFieldCartUpdate({
      formRef: sectionRef,
      checkoutForm: formHandle,
      event: e,
      prevFieldValue: prevCustomFieldRef,
      formFieldName: 'customFieldValue',
      updateConsumerCart
    })
  }
  useFocusOutListener({ sectionRef, onFocusOut: handleFocusOut })

  return (
    <Content data-testid='custom-field' ref={sectionRef}>
      <Label data-testid='custom-field-label'>
        <Title>{fieldName} </Title>
        <OptionalityText>{required ? 'Required' : 'Optional'}</OptionalityText>
      </Label>
      <FormInput
        variant='textarea'
        id='customFieldValue'
        data-testid='custom-field-textarea'
        value={customFieldValue || ''}
        name='customFieldValue'
        labelText='Custom Field'
        showError={!!error}
        placeholder={placeholder || ''}
        formRef={register('customFieldValue', {
          required: required ? 'Please enter a value' : undefined,
          validate: inputCharactersLimitValidator('field')
        })}
        scrollSettings={{
          scrollOnFocus: true,
          scrollToElementId: 'custom-field-section'
        }}
      />
    </Content>
  )
}

const Content = styled.div(
  (): CSSObject => ({
    display: 'flex',
    flexDirection: 'column'
  })
)

const Label = styled.div(() => ({
  display: 'flex',
  alignItems: 'center',
  '> p': {
    margin: '0 0 16px 0'
  }
}))

const Title = styled.p(() => ({
  fontSize: '16px',
  fontWeight: 500,
  whiteSpace: 'pre-wrap'
}))

const OptionalityText = styled.p(() => ({
  fontSize: '14px',
  fontStyle: 'italic',
  color: '#262626'
}))

export default CustomField
